// The Planet XXXXXX (Contact) System
// all geo, materials, and positioning handled here
// orbit paths too?
import * as THREE from "three"
import Experience from "../Experience"

import CustomShaderMaterial from "three-custom-shader-material/vanilla"

import planetContactVertexShader from "../../shaders/planetContact/planetContactVertex.glsl"
import planetContactFragmentShader from "../../shaders/planetContact/planetContactFragment.glsl"

import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js"


// Planet Contact
export class PlanetContact{
    constructor(){
        this.experience = new Experience()
        this.time = this.experience.time
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.orbitPathFile = this.resources.items.orbitModel
        this.orbitPath = this.orbitPathFile.children[0]
        this.orbits = []
        this.planetGroup = new THREE.Group()
        this.planetAngle = 14
        this.planetTilt = this.planetAngle * (Math.PI / 180)
        this.orbitDistance = 40
        this.orbitSpeed = 0.0
        this.offsetRadians = 0.75 // 1 = 180 degrees

        // File containing models for moons
        this.moonFile = this.resources.items.contactModels
        this.moonAMesh01 = this.moonFile.children[6]
        this.moonAMesh02 = this.moonFile.children[7]

        this.moonBMesh01 = this.moonFile.children[2]
        this.moonBMesh02 = this.moonFile.children[3]

        this.moonCMesh01 = this.moonFile.children[0]
        this.moonCMesh02 = this.moonFile.children[1]

        this.moonDMesh01 = this.moonFile.children[4]
        this.moonDMesh02 = this.moonFile.children[5]

        this.moonAOrbitDistance = 5
        this.moonBOrbitDistance = 8
        this.moonCOrbitDistance = 12
        this.moonDOrbitDistance = 15
        
        // Subscribe to the tick function
        this.time.addEventListener("tick", ()=>{
            this.rotate()
            this.orbit()
        })
        
        this.setGeometry()
        this.setTextures()
        this.setMaterials()
        this.setMesh()
        this.setPosition()
        this.setOrbitPaths()
    }

    setGeometry(){
        let photographyPlanetGeo = new THREE.IcosahedronGeometry(2, 40)
        this.geometry = mergeVertices(photographyPlanetGeo)
        this.geometry.computeTangents()
    }

    setTextures(){
        
    }

    setMaterials(){
        this.lineMat = new THREE.LineBasicMaterial({
            color: 0x555555,
            linewidth:1
        })

        this.unlitWhiteMat = new THREE.MeshBasicMaterial({
            color: 0xffffff,
        })

        this.colors = {}
        this.colors.colorA = '#4fa5d8'
        this.colors.colorB = '#0855b1'
        this.colors.roughnessLow = '0.1'
        this.colors.roughnessHigh = '0.9'

        this.uniforms = {}
        this.uniforms.uPositionFrequency = new THREE.Uniform(0.4),
        this.uniforms.uStrength = new THREE.Uniform(0.1),
        this.uniforms.uColorLow = new THREE.Uniform(new THREE.Color(this.colors.colorA)),
        this.uniforms.uColorHigh = new THREE.Uniform(new THREE.Color(this.colors.colorB))
        
        this.material = new CustomShaderMaterial({
            // CustomShaderMaterial
            baseMaterial: THREE.MeshPhysicalMaterial,
            //baseMaterial: THREE.MeshStandardMaterial,
            vertexShader: planetContactVertexShader,
            fragmentShader: planetContactFragmentShader,
            uniforms: this.uniforms,
            silent: true,
            
            //MeshPhysicalMaterial
            metalness: 0.0,
            roughness: 0.0,
            color: 0xffffff,
            emissive: 0x000000,
            transmission: 0.0,
            ior: 1.5,
            thickness: 1.5,
            transparent: false,
            wireframe: false,
            depthWrite: true,
            depthTest: true,
        })
    }

    setMesh(){
        this.mesh = new THREE.Mesh(
            this.geometry,
            this.material
        )
        this.mesh.castShadow = true
        this.mesh.receiveShadow = true
        this.mesh.rotation.order = "ZYX"
        this.mesh.rotation.z = this.planetTilt
        this.scene.add(this.mesh)
    }

    setPosition(){
        this.offset = Math.PI * this.offsetRadians
        this.planetGroup.position.x = this.orbitDistance * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset)
        this.planetGroup.position.z = this.orbitDistance * Math.cos(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset)
        this.scene.add(this.planetGroup)
    }

    setOrbitPaths(){
        // Duplicates and sets position of fbx nurbs circle to reprresent planet paths
        this.orbitPath.material = this.lineMat
        let tempPlanetPath
        let tempMoonAPath
        let tempMoonBPath
        let tempMoonCPath
        let tempMoonDPath
        
        for(let i = 0; i < 5; i ++){
            const childClone = this.orbitPath.clone()
            this.scene.add(childClone)
            this.orbits.push(childClone)
        }

        [
            tempPlanetPath,
            tempMoonAPath,
            tempMoonBPath,
            tempMoonCPath,
            tempMoonDPath
        ] = this.orbits

        // Set orbit transforms
        this.planetPath = tempPlanetPath
        this.planetPath.scale.set(this.orbitDistance, this.orbitDistance, this.orbitDistance)

        this.moonAPath = tempMoonAPath
        this.moonAPath.scale.set(this.moonAOrbitDistance, this.moonAOrbitDistance, this.moonAOrbitDistance)
        this.moonAPath.rotation.set(0,0,this.planetTilt)

        this.moonBPath = tempMoonBPath
        this.moonBPath.scale.set(this.moonBOrbitDistance, this.moonBOrbitDistance, this.moonBOrbitDistance)
        this.moonBPath.rotation.set(0,0,this.planetTilt)

        this.moonCPath = tempMoonCPath
        this.moonCPath.scale.set(this.moonCOrbitDistance, this.moonCOrbitDistance, this.moonCOrbitDistance)
        this.moonCPath.rotation.set(0,0,this.planetTilt)

        this.moonDPath = tempMoonDPath
        this.moonDPath.scale.set(this.moonDOrbitDistance, this.moonDOrbitDistance, this.moonDOrbitDistance)
        this.moonDPath.rotation.set(0,0,this.planetTilt)

    }

    rotate(){
        this.mesh.rotation.y = (this.time.elapsed * 0.001) * 0.08
    }

    orbit(){
        if(this.planetGroup){
            this.mesh.position.x = this.planetGroup.position.x
            this.mesh.position.z = this.planetGroup.position.z
        }

        if(this.moonAPath){
            this.moonAPath.position.x = this.planetGroup.position.x
            this.moonAPath.position.z = this.planetGroup.position.z
        }

        if(this.moonBPath){
            this.moonBPath.position.x = this.planetGroup.position.x
            this.moonBPath.position.z = this.planetGroup.position.z
        }

        if(this.moonCPath){
            this.moonCPath.position.x = this.planetGroup.position.x
            this.moonCPath.position.z = this.planetGroup.position.z
        }

        if(this.moonDPath){
            this.moonDPath.position.x = this.planetGroup.position.x
            this.moonDPath.position.z = this.planetGroup.position.z
        }
    }
}

export class MoonContactEmail{
    constructor(parentPlanet){
        this.experience = new Experience()
        this.time = this.experience.time
        this.scene = this.experience.scene
        this.resources = this.experience.resources

        this.parentPlanet = parentPlanet
        this.planetGroup = this.parentPlanet.planetGroup
        this.planetTilt = this.parentPlanet.planetTilt
        this.orbitDistance = this.parentPlanet.moonAOrbitDistance
        this.orbitSpeed = 0.1
        this.offsetRadians = 1.5 // 1 = 180 degrees

        //Need a group for this moon's multiple meshes
        this.moonGroup = new THREE.Group()
        
        // Subscribe to the tick function
        this.time.addEventListener("tick", ()=>{
            this.rotate()
            this.orbit()
        })

        this.setGeometry()
        this.setTextures()
        this.setMaterials()
        this.setMesh()
        this.setPosition()
    }

    setGeometry(){
    }

    setTextures(){
        
    }

    setMaterials(){
        this.material01 = new THREE.MeshPhysicalMaterial({
            color: 0xff0000,
            roughness: 0.2,
            metalness: 0.4
        })
    }

    setMesh(){
        this.mesh01 = this.parentPlanet.moonAMesh01
        this.mesh01.material = this.material01
        this.mesh01.castShadow = true
        this.mesh01.receiveShadow = true
        this.scene.add(this.mesh01)

        this.mesh02 = this.parentPlanet.moonAMesh02
        this.mesh02.material = this.parentPlanet.unlitWhiteMat
        this.mesh02.castShadow = true
        this.mesh02.receiveShadow = true
        this.scene.add(this.mesh02)

        this.moonGroup.scale.set(0.12, 0.12, 0.12)
        this.moonGroup.rotation.order = "ZYX"
        this.moonGroup.rotation.z = this.planetTilt
        this.moonGroup.add(this.mesh01, this.mesh02)
    }
    
    setPosition(){
        this.orbitPlane = (Math.sin(this.planetTilt) * this.orbitDistance) / (Math.tan(this.planetTilt))
        this.offset = Math.PI * this.offsetRadians
        this.scene.add(this.moonGroup)
    }
    
    rotate(){
        this.moonGroup.rotation.y = (this.time.elapsed * 0.001) * 0.3
    }

    orbit(){

        if(this.planetGroup){
            this.moonGroup.position.x = this.planetGroup.position.x + (this.orbitPlane * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.z = this.planetGroup.position.z + (this.orbitDistance * Math.cos(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.y = this.planetGroup.position.y + (this.orbitDistance * Math.sin(this.planetTilt) * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
        }
        else{
            console.log("no Planet Group")
        }
    }
}

export class MoonContactYouTube{
    constructor(parentPlanet){
        this.experience = new Experience()
        this.time = this.experience.time
        this.scene = this.experience.scene
        this.resources = this.experience.resources

        this.parentPlanet = parentPlanet
        this.planetGroup = this.parentPlanet.planetGroup
        this.planetTilt = this.parentPlanet.planetTilt
        this.orbitDistance = this.parentPlanet.moonBOrbitDistance
        this.orbitSpeed = 0.09
        this.offsetRadians = 1.25 // 1 = 180 degrees

        //Need a group for this moon's multiple meshes
        this.moonGroup = new THREE.Group()
        
        // Subscribe to the tick function
        this.time.addEventListener("tick", ()=>{
            this.rotate()
            this.orbit()
        })

        this.setGeometry()
        this.setTextures()
        this.setMaterials()
        this.setMesh()
        this.setPosition()
    }

    setGeometry(){
        // let moonAnimationRiggingGeo = new THREE.IcosahedronGeometry(1, 50)
        // this.geometry = mergeVertices(moonAnimationRiggingGeo)
        // this.geometry.computeTangents()
    }
    setTextures(){
        
    }

    setMaterials(){
        // this.material01 = new THREE.MeshBasicMaterial({
        //     color: 0xffffff,
        // })

        this.material02 = new THREE.MeshPhysicalMaterial({
            color: 0xff0000,
            roughness: 0.2,
            metalness: 0.4
        })
    }

    setMesh(){
        this.mesh01 = this.parentPlanet.moonBMesh01
        this.mesh01.material = this.parentPlanet.unlitWhiteMat
        this.mesh01.castShadow = true
        this.mesh01.receiveShadow = true
        this.scene.add(this.mesh01)

        this.mesh02 = this.parentPlanet.moonBMesh02
        this.mesh02.material = this.material02
        this.mesh02.castShadow = true
        this.mesh02.receiveShadow = true
        this.scene.add(this.mesh02)

        this.moonGroup.scale.set(0.25, 0.25, 0.25)
        this.moonGroup.rotation.order = "ZYX"
        this.moonGroup.rotation.z = this.planetTilt
        this.moonGroup.add(this.mesh01, this.mesh02)

    }
    
    setPosition(){
        this.orbitPlane = (Math.sin(this.planetTilt) * this.orbitDistance) / (Math.tan(this.planetTilt))
        this.offset = Math.PI * this.offsetRadians
        this.scene.add(this.moonGroup)
    }
    
    rotate(){
        this.moonGroup.rotation.y = (this.time.elapsed * 0.001) * 0.25
    }

    orbit(){
        if(this.planetGroup){
            this.moonGroup.position.x = this.planetGroup.position.x + (this.orbitPlane * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.z = this.planetGroup.position.z + (this.orbitDistance * Math.cos(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.y = this.planetGroup.position.y + (this.orbitDistance * Math.sin(this.planetTilt) * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
        }
        else{
            console.log("no Planet Group")
        }
    }
}

export class MoonContactInstagram{
    constructor(parentPlanet){
        this.experience = new Experience()
        this.time = this.experience.time
        this.scene = this.experience.scene
        this.resources = this.experience.resources

        this.parentPlanet = parentPlanet
        this.planetGroup = this.parentPlanet.planetGroup
        this.planetTilt = this.parentPlanet.planetTilt
        this.orbitDistance = this.parentPlanet.moonCOrbitDistance
        this.orbitSpeed = 0.01
        this.offsetRadians = 0.6 // 1 = 180 degrees
        
        //Need a group for this moon's multiple meshes
        this.moonGroup = new THREE.Group()

        // Subscribe to the tick function
        this.time.addEventListener("tick", ()=>{
            this.rotate()
            this.orbit()
        })

        this.setGeometry()
        this.setTextures()
        this.setMaterials()
        this.setMesh()
        this.setPosition()
    }

    setGeometry(){
        // let moonAnimationAnimationGeo = new THREE.IcosahedronGeometry(1, 50)
        // this.geometry = mergeVertices(moonAnimationAnimationGeo)
        // this.geometry.computeTangents()
    }
    setTextures(){
        
    }

    setMaterials(){
        this.material01 = new THREE.MeshPhysicalMaterial({
            color: 0xff0000,
            roughness: 0.2,
            metalness: 0.4
        })

        // this.material02 = new THREE.MeshBasicMaterial({
        //     color: 0xffffff,
        // })
    }

    setMesh(){
        this.mesh01 = this.parentPlanet.moonCMesh01
        this.mesh01.material = this.material01
        this.mesh01.castShadow = true
        this.mesh01.receiveShadow = true
        this.scene.add(this.mesh01)

        this.mesh02 = this.parentPlanet.moonCMesh02
        this.mesh02.material = this.parentPlanet.unlitWhiteMat
        this.mesh02.castShadow = true
        this.mesh02.receiveShadow = true
        this.scene.add(this.mesh02)

        this.moonGroup.scale.set(0.12, 0.12, 0.12)
        this.moonGroup.rotation.order = "ZYX"
        this.moonGroup.rotation.z = this.planetTilt
        this.moonGroup.add(this.mesh01, this.mesh02)

    }
    
    setPosition(){
        this.orbitPlane = (Math.sin(this.planetTilt) * this.orbitDistance) / (Math.tan(this.planetTilt))
        this.offset = Math.PI * this.offsetRadians
        this.scene.add(this.moonGroup)
    }
    
    rotate(){
        this.moonGroup.rotation.y = (this.time.elapsed * 0.001) * 0.25
    }

    orbit(){

        if(this.planetGroup){
            this.moonGroup.position.x = this.planetGroup.position.x + (this.orbitPlane * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.z = this.planetGroup.position.z + (this.orbitDistance * Math.cos(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.y = this.planetGroup.position.y + (this.orbitDistance * Math.sin(this.planetTilt) * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
        }
        else{
            console.log("no Planet Group")
        }
    }
}

export class MoonContactLinkedIn{
    constructor(parentPlanet){
        this.experience = new Experience()
        this.time = this.experience.time
        this.scene = this.experience.scene
        this.resources = this.experience.resources

        this.parentPlanet = parentPlanet
        this.planetGroup = this.parentPlanet.planetGroup
        this.planetTilt = this.parentPlanet.planetTilt
        this.orbitDistance = this.parentPlanet.moonDOrbitDistance
        this.orbitSpeed = 0.01
        this.offsetRadians = 0.5

        //Need a group for this moon's multiple meshes
        this.moonGroup = new THREE.Group()
        
        // Subscribe to the tick function
        this.time.addEventListener("tick", ()=>{
            this.rotate()
            this.orbit()
        })

        //console.log("Moon - Photography - Misc")
        this.setGeometry()
        this.setTextures()
        this.setMaterials()
        this.setMesh()
        this.setPosition()
    }

    setGeometry(){
        // let moonAnimationRiggingGeo = new THREE.IcosahedronGeometry(1, 50)
        // this.geometry = mergeVertices(moonAnimationRiggingGeo)
        // this.geometry.computeTangents()
    }
    setTextures(){
        
    }

    setMaterials(){
        this.material01 = new THREE.MeshPhysicalMaterial({
            color: 0x0078d4,
            roughness: 0.2,
            metalness: 0.4
        })

        // this.material02 = new THREE.MeshBasicMaterial({
        //     color: 0xffffff,
        // })
    }

    setMesh(){
        this.mesh01 = this.parentPlanet.moonDMesh01
        this.mesh01.material = this.material01
        this.mesh01.castShadow = true
        this.mesh01.receiveShadow = true
        this.scene.add(this.mesh01)

        this.mesh02 = this.parentPlanet.moonDMesh02
        this.mesh02.material = this.parentPlanet.unlitWhiteMat
        this.mesh02.castShadow = true
        this.mesh02.receiveShadow = true
        this.scene.add(this.mesh02)

        this.moonGroup.scale.set(0.12, 0.12, 0.12)
        this.moonGroup.rotation.order = "ZYX"
        this.moonGroup.rotation.z = this.planetTilt
        this.moonGroup.add(this.mesh01, this.mesh02)

    }
    
    setPosition(){
        this.orbitPlane = (Math.sin(this.planetTilt) * this.orbitDistance) / (Math.tan(this.planetTilt))
        this.offset = Math.PI * this.offsetRadians
        this.scene.add(this.moonGroup)
    }
    
    rotate(){
        this.moonGroup.rotation.y = (this.time.elapsed * 0.001) * 0.25
    }

    orbit(){

        if(this.planetGroup){
            this.moonGroup.position.z = this.planetGroup.position.z + (this.orbitDistance * Math.cos(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.y = this.planetGroup.position.y + (this.orbitDistance * Math.sin(this.planetTilt) * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
            this.moonGroup.position.x = this.planetGroup.position.x + (this.orbitPlane * Math.sin(((this.time.elapsed * 0.001) * this.orbitSpeed) + this.offset))
        }
        else{
            console.log("no Planet Group")
        }
    }
}